import { useState, type CSSProperties, type JSX } from 'react';
import { Badge, Button, Field, Input } from '@viniciomendesr/rayrovisk-design-system';
import { ApiError } from '../api/client';
import { saveReadingLead } from '../api/profileReading';
import { OFFERS, brlWithCents, bundleTotal, checkout } from './salesContent';

const OFFER = OFFERS.generator;

const heading: CSSProperties = {
  margin: 0,
  font: '400 21px/1.25 var(--rvk-font-expressive)',
  color: 'var(--rvk-text-primary)',
  textWrap: 'balance'
};

const fine: CSSProperties = {
  font: '400 12px/1.5 var(--rvk-font-functional)',
  color: 'var(--rvk-text-tertiary)'
};

/**
 * "As 3 cenas que faltam", "A cena que falta", or the whole thing when the
 * roteiro never arrived and there is no count to name.
 *
 * The count comes from the reading, so it cannot disagree with the "mais N
 * cenas" line the preview already printed.
 */
function missingScenes(remaining: number | undefined): string {
  if (remaining === undefined) {
    return 'O roteiro inteiro, com a legenda';
  }
  return remaining === 1
    ? 'A cena que falta, com a legenda'
    : `As ${remaining} cenas que faltam, com a legenda`;
}

export interface ReadingOfferProps {
  /** Stored with the address, so a lead is traceable to what she was reading. */
  handle: string;
  /** Undefined when the roteiro never arrived, so there is no count to name. */
  remainingScenes?: number;
  onEvent?: (name: string, properties?: Record<string, unknown>) => void;
  /**
   * Fires once the address is stored, never before: a lead who typed and gave
   * up must not become a person. ADR-0025 — the caller attaches it to her
   * existing anonymous profile rather than identifying her under a new id.
   */
  onEmailCaptured?: (email: string) => void;
  /** Closes the dialog, for the link that sends her to the price section. */
  onClose?: () => void;
}

/**
 * What the free reading is for.
 *
 * The ask comes after the reading rather than before it, which is the whole
 * difference between a lead magnet and a toll gate: by the time the field
 * appears, the page has already handed her the post, the three reasons and two
 * scenes.
 *
 * **The address buys the reading, not a discount and not the rest of the
 * roteiro** (ADR-0037). Until 2026-09-01 it bought 50% off; the offer is one
 * price now, the same R$ 37,00 the page shows, so what the address can honestly
 * buy is the analysis landing in her inbox. The scenes the reading withheld
 * stay withheld: `scriptFrameFor` keeps them because they are the thing being
 * sold, and an address is not a purchase.
 *
 * There is no countdown. The design carried a five-minute timer that restarts
 * on reload, which is a claim about an expiry that does not exist — and an
 * offer announced with a false deadline is exactly what CDC art. 37 is about.
 */
export function ReadingOffer({
  handle,
  remainingScenes,
  onEvent,
  onEmailCaptured,
  onClose
}: ReadingOfferProps): JSX.Element {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<{ email: string; sent: boolean }>();

  async function unlock(): Promise<void> {
    setSaving(true);
    setError(undefined);
    try {
      const address = email.trim();
      const result = await saveReadingLead(address, handle);
      setSaved({ email: address, sent: result.sent });
      onEmailCaptured?.(address);
      onEvent?.('reading_lead_saved', { sent: result.sent });
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não consegui guardar agora. Tente de novo em alguns minutos.'
      );
    } finally {
      setSaving(false);
    }
  }

  if (saved) {
    return (
      <Offer
        saved={saved}
        remainingScenes={remainingScenes}
        onEvent={onEvent}
        onClose={onClose}
      />
    );
  }

  return (
    <form
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        padding: 20,
        background: 'var(--rvk-surface-1)',
        border: '1px solid var(--rvk-border-default)',
        borderRadius: 'var(--rvk-radius-xl)'
      }}
      onSubmit={(event) => {
        event.preventDefault();
        void unlock();
      }}
    >
      {/* Names what she wants, which is the scenes — then says where they are,
          which is the access, and what the address actually buys, which is the
          reading she just read. */}
      <h3 style={heading}>{missingScenes(remainingScenes)}</h3>
      <p
        style={{
          margin: 0,
          font: '400 14.5px/1.55 var(--rvk-font-functional)',
          color: 'var(--rvk-text-primary)',
          maxWidth: '52ch',
          textWrap: 'pretty'
        }}
      >
        Vêm no acesso completo, junto com o que mudar na próxima gravação. Deixe o seu e-mail e o
        Roteirovisk manda a análise que você acabou de ler.
      </p>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 240px', minWidth: 200 }}>
          <Field label="Seu e-mail" labelHidden error={error}>
            <Input
              size="lg"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="voce@email.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
        </div>
        <Button type="submit" variant="primary" size="lg" loading={saving}>
          Receber a análise
        </Button>
      </div>

      <span style={fine}>Sem custo. O e-mail é só para a análise chegar.</span>
    </form>
  );
}

function Row({ children }: { children: string }): JSX.Element {
  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 14,
        padding: '11px 0',
        borderBottom: '1px solid var(--rvk-border-subtle)'
      }}
    >
      <span style={{ font: '400 14px/1.4 var(--rvk-font-functional)' }}>{children}</span>
      <span
        style={{
          flex: 'none',
          font: '500 13.5px/1.4 var(--rvk-font-functional)',
          color: 'var(--rvk-accent-default)',
          whiteSpace: 'nowrap'
        }}
      >
        Incluído
      </span>
    </span>
  );
}

function Offer({
  saved,
  remainingScenes,
  onEvent,
  onClose
}: {
  saved: { email: string; sent: boolean };
  remainingScenes: number | undefined;
  onEvent?: (name: string, properties?: Record<string, unknown>) => void;
  onClose?: () => void;
}): JSX.Element {
  const price = brlWithCents(OFFER.price);
  const href = checkout('PROMPT_GENERATOR');

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        padding: 20,
        background: 'var(--rvk-color-cream-50)',
        border: '2px solid var(--rvk-accent-default)',
        borderRadius: 'var(--rvk-radius-xl)'
      }}
    >
      {/* A badge is a label, not a banner: without this it stretches to the
          column's width and reads as a section header. */}
      <span style={{ alignSelf: 'flex-start' }}>
        <Badge tone="accent" variant="solid" size="sm">
          {saved.sent ? 'Análise enviada' : 'E-mail guardado'}
        </Badge>
      </span>

      {/* Never "mandei" when nothing left. The service says which happened, and
          a message that failed is a message she should not go looking for. */}
      <p
        style={{
          margin: 0,
          font: '400 14px/1.55 var(--rvk-font-functional)',
          color: 'var(--rvk-text-primary)'
        }}
      >
        {saved.sent
          ? `A análise saiu para ${saved.email}.`
          : `Guardei o seu e-mail. A análise continua aqui na tela.`}
      </p>

      <h3 style={{ ...heading, fontSize: 24 }}>
        O roteiro é seu. O acesso sai por {price} em vez de {brlWithCents(bundleTotal())}.
      </h3>
      <p
        style={{
          margin: 0,
          font: '400 14px/1.55 var(--rvk-font-functional)',
          color: 'var(--rvk-text-primary)',
          maxWidth: '52ch',
          textWrap: 'pretty'
        }}
      >
        Um post você já tirou daqui. Os 200 prompts e os 100 roteiros são para as outras semanas.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Row>A análise que você acabou de ver</Row>
        <Row>{missingScenes(remainingScenes)}</Row>
        <Row>200 prompts customizáveis e 100 roteiros no gerador</Row>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span
          style={{
            font: '400 15px/1 var(--rvk-font-functional)',
            color: 'var(--rvk-text-tertiary)',
            textDecoration: 'line-through'
          }}
        >
          {brlWithCents(bundleTotal())}
        </span>
        <span
          style={{ font: '400 36px/1 var(--rvk-font-expressive)', color: 'var(--rvk-text-primary)' }}
        >
          {price}
        </span>
        <span style={fine}>uma vez, ou em até 12×</span>
      </div>

      <span style={{ ...fine, color: 'var(--rvk-text-secondary)' }}>
        A gravação continua sendo sua. Se não servir, você tem 7 dias para pedir o dinheiro de
        volta.
      </span>

      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener"
          style={{ display: 'block' }}
          onClick={() =>
            onEvent?.('checkout_initiated', {
              product: 'PROMPT_GENERATOR',
              placement: 'profile-reading'
            })
          }
        >
          <Button variant="primary" size="lg" fullWidth>
            Quero acesso por {price}
          </Button>
        </a>
      ) : null}

      {onClose ? (
        <a
          href="#precos"
          onClick={onClose}
          style={{
            alignSelf: 'center',
            font: '400 13px/1.4 var(--rvk-font-functional)',
            color: 'var(--rvk-text-secondary)'
          }}
        >
          Ver tudo o que vem no acesso
        </a>
      ) : null}
    </div>
  );
}
